import { createTheme } from '@mui/material/styles';

const theme = createTheme({
    palette: {
        // Cor principal (brand color)
        primary: {
            main: '#1F83FF', // Cor principal
            light: '#289AFF', // Azul claro
            dark: '#1969CC', // Azul escuro
            contrastText: '#F5F5F5', // Texto sobre o azul
        },
        // Tons personalizados de azul claro
        blueLight: {
            100: '#EDF8FF',
            200: '#B9E3FF',
            300: '#88D4FF',
            400: '#50BAFF',
            500: '#289AFF',
        },
        // Tons personalizados de azul escuro
        blueDark: {
            100: '#1969CC',
            200: '#134F99',
            300: '#0C3466',
            400: '#061A33',
            500: '#030D1A',
        },
        // Tons personalizados de branco
        white: {
            100: '#FFFFFF',
            200: '#F5F5F5',
            300: '#E0E0E0',
            400: '#CCCCCC',
        },
        // Tons personalizados de cinza escuro
        dark: {
            100: '#A3A3A3',
            200: '#666666',
            300: '#292929',
            400: '#0B0B0B',
        },
        // Estados padrão
        success: {
            main: '#97CC04', // Verde principal
            light: '#C1E068',
            dark: '#C1E068',
            contrastText: '#ffffff',
        },
        warning: {
            main: '#EEB902', // Amarelo principal
            light: '#F5D567',
            dark: '#F8E39A',
            contrastText: '#ffffff',
        },
        error: {
            main: '#E63946', // Vermelho principal
            light: '#F5B0B5',
            dark: '#F08890',
            contrastText: '#ffffff',
        },
        background: {
            default: '#F4F4F4', // Cor do fundo geral
            paper: '#ffffff', // Cor do fundo de cards ou elementos "paper"
        },
        text: {
            primary: '#333333', // Cor padrão do texto
            secondary: '#757575', // Cor secundária
            disabled: '#BDBDBD', // Texto desativado
        },
    },
    typography: {
        fontFamily: "'Poppins', Arial, sans-serif",
        h1: {
            fontWeight: 700,
            fontSize: '2.5rem',
            lineHeight: 1.2,
        },
        h2: {
            fontWeight: 600,
            fontSize: '2rem',
            lineHeight: 1.3,
        },
        h3: {
            fontWeight: 500,
            fontSize: '1.75rem',
            lineHeight: 1.4,
        },
        body1: {
            fontWeight: 400,
            fontSize: '1rem',
            lineHeight: 1.5,
        },
        body2: {
            fontWeight: 400,
            fontSize: '0.875rem',
            lineHeight: 1.6,
        },
        button: {
            textTransform: 'none', // Remove uppercase padrão
            fontWeight: 600,
        },
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 8, // Botões arredondados
                    textTransform: 'none', // Sem uppercase
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: '#1F83FF', // Personaliza a AppBar
                },
            },
        },
        MuiTable: {
            styleOverrides: {
                root: {
                    borderCollapse: 'separate', // Permite bordas externas e internas separadas
                    borderSpacing: 0, // Remove espaçamento padrão
                    border: '2px solid #E0E0E0', // Borda externa (extremidades)
                    borderRadius: '12px', // Bordas arredondadas
                    overflow: 'hidden', // Garante que o conteúdo respeite o arredondamento
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    padding: '12px 16px',
                    fontFamily: 'Poppins',
                    fontSize: '14px',
                    fontWeight: '400',
                    lineHeight: '21px',
                    borderBottom: '1px solid #E0E0E0', // Apenas bordas horizontais (entre as linhas)
                    borderRight: 'none', // Remove bordas verticais entre colunas
                },
                head: {
                    backgroundColor: '#f5f5f5',
                    fontFamily: 'Poppins',
                    fontSize: '14px',
                    fontWeight: '400',
                    lineHeight: '21px',
                    borderBottom: '2px solid #E0E0E0', // Borda destacada para o cabeçalho
                },
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    '&:last-child td': {
                        borderBottom: 'none', // Remove a borda inferior da última linha
                    },
                    '&:nth-of-type(odd)': {
                        backgroundColor: '#f9f9f9', // Fundo alternado para linhas ímpares
                    },
                    '&:hover': {
                        backgroundColor: '#e0f7fa', // Fundo ao passar o mouse
                    },
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    padding: '0 8px', // Controle de padding interno
                    fontFamily: 'Poppins',
                    fontSize: '12px',
                    fontWeight: 400,
                    lineHeight: '18px',
                    textTransform: 'none', // Evita texto em uppercase
                    borderRadius: '12px', // Chips arredondados
                },
                sizeSmall: {
                    padding: '0 4px', // Padding menor para chips pequenos
                    fontSize: '10px',
                },
                sizeMedium: {
                    padding: '0 8px',
                    fontSize: '12px',
                },
                colorPrimary: {
                    backgroundColor: '#B9E3FF', // Fundo para chips primários
                    color: '#1969CC',
                },
                deleteIcon: {
                    color: '#FFFFFF', // Cor do ícone de delete
                    '&:hover': {
                        color: '#E63946', // Cor ao passar o mouse no ícone de delete
                    },
                },
                label: {
                    paddingLeft: '6px', // Espaçamento interno do texto
                    paddingRight: '6px',
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: '10px', // Rounded corners
                },
                input: {
                    padding: '10px 14px', // Adjust padding for text alignment
                    fontFamily: 'Poppins',
                    fontSize: '14px',
                    color: '#333333', // Text color
                    '&[type="datetime-local"]': {
                        padding: '10px 14px', // Custom padding for datetime-local
                        fontFamily: 'Poppins',
                        fontSize: '14px',
                    },
                },
            },
        },

        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        minHeight: '47px',
                        borderRadius: '10px', // Rounded corners
                        '& fieldset': {
                            borderColor: '#CCCCCC', // Default border color
                        },
                        '&:hover fieldset': {
                            borderColor: '#999999', // Border color on hover
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: '#1F83FF', // Border color on focus
                        },
                    },
                    '& .MuiOutlinedInput-input': {
                        padding: '12px 14px', // Default padding for vertical alignment
                        fontFamily: 'Poppins',
                        fontSize: '14px',
                        color: '#333333', // Text color
                        '&[type="datetime-local"]': {
                            padding: '10px 14px', // Custom padding for datetime-local
                        },
                    },
                    '& .MuiInputLabel-root': {
                        transform: 'translate(14px, 14px) scale(1)', // Center label initially
                        fontSize: '14px',
                        fontFamily: 'Poppins',
                        color: '#666666',
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                        transform: 'translate(14px, -6px) scale(0.75)', // Move label up on focus
                        color: '#1F83FF',
                    },
                    '& .MuiInputLabel-root.MuiFormLabel-filled': {
                        transform: 'translate(14px, -6px) scale(0.75)', // Move label up when filled
                        color: '#666666', // Default label color when filled
                    },
                },
            },
        },

        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: '10px', // Rounded corners
                },
                input: {
                    padding: '12px 14px', // Ensure input text is vertically aligned
                },
            },
        },

        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: '10px', // Bordas arredondadas
                    height: '32px', // Altura fixa
                    fontFamily: 'Poppins', // Fonte do texto
                    fontSize: '14px', // Tamanho da fonte
                    fontWeight: 500, // Peso do texto
                    textTransform: 'none', // Remove o uppercase padrão
                    padding: '4px 16px', // Padding interno
                },
                containedPrimary: {
                    backgroundColor: '#1F83FF', // Fundo para o botão primário
                    color: '#FFFFFF', // Texto branco
                    '&:hover': {
                        backgroundColor: '#1969CC', // Cor ao passar o mouse
                    },
                },
                outlinedPrimary: {
                    border: '1px solid #1F83FF', // Cor da borda
                    color: '#1F83FF', // Cor do texto
                    backgroundColor: '#FFFFFF', // Fundo branco
                    '&:hover': {
                        border: '1px solid #1969CC', // Cor da borda ao hover
                        color: '#1969CC', // Cor do texto ao hover
                        backgroundColor: '#F5F5F5', // Fundo sutil ao hover
                    },
                },
                disabled: {
                    backgroundColor: '#E0E0E0', // Fundo desabilitado
                    color: '#BDBDBD', // Texto desabilitado
                },
            },
        },
        MuiLink: {
            styleOverrides: {
                root: {
                    fontFamily: 'Poppins',
                    fontSize: '12px',
                    fontWeight: 400,
                    color: '#1F83FF',
                    textDecoration: 'none',
                },
            },
        },
        MuiToggleButton: {
            styleOverrides: {
                root: {
                    borderRadius: '8px',
                    fontFamily: 'Poppins',
                    fontSize: '14px',
                    fontWeight: 600,
                    textTransform: 'none',
                    padding: '4px 16px',
                    backgroundColor: '#EDF8FF',
                    border: 'none',
                    color: '#1F83FF',
                    '&.Mui-selected': {
                        backgroundColor: '#1F83FF',
                        color: '#FFFFFF',
                        border: 'none',
                        '&:hover': {
                            backgroundColor: '#1969CC',
                        },
                    },
                    '&:hover': {
                        backgroundColor: '#F5F5F5',
                    },
                },
            },
        },
        MuiToggleButtonGroup: {
            styleOverrides: {
                root: {
                    // gap: '8px',
                },
            },
        },
    },
});

export default theme;
